import { useQuery } from '@tanstack/react-query';
import { getDependencies, getPackage, getProjectHealth, getVersion } from './api';

/**
 * TanStack Query hooks for package data.
 * queryKeys follow [resource, ...identifiers] convention so that fine-grained
 * invalidation is possible later (e.g. invalidate a single package on mutation).
 */

export function usePackage(system: string | undefined, name: string | undefined) {
  return useQuery({
    queryKey: ['packages', system, name],
    queryFn: () => getPackage(system!, name!),
    enabled: Boolean(system && name),
    staleTime: 60_000,
  });
}

export function useVersionDetail(
  system: string | undefined,
  name: string | undefined,
  version: string | undefined,
) {
  return useQuery({
    queryKey: ['packages', system, name, 'versions', version],
    queryFn: () => getVersion(system!, name!, version!),
    enabled: Boolean(system && name && version),
    staleTime: 60_000,
  });
}

/**
 * Resolved dependency graph for a version. Gated behind `enabled` so it only
 * runs after the user clicks "Fetch dependencies" — the graph can be large and
 * we don't want to fetch it on every version view.
 */
export function useDependencies(
  system: string | undefined,
  name: string | undefined,
  version: string | undefined,
  enabled: boolean,
) {
  return useQuery({
    queryKey: ['packages', system, name, 'versions', version, 'dependencies'],
    queryFn: () => getDependencies(system!, name!, version!),
    enabled: enabled && Boolean(system && name && version),
    staleTime: 60_000,
  });
}

/**
 * Project health for a relatedProjectId (e.g. "github.com/facebook/react").
 * Gated behind the presence of a non-null id. A 404 is surfaced as an error
 * (callers should render nothing when it occurs).
 */
export function useProjectHealth(id: string | null | undefined) {
  return useQuery({
    queryKey: ['projects', id],
    queryFn: () => getProjectHealth(id!),
    enabled: Boolean(id),
    staleTime: 5 * 60_000,
    retry: false,
  });
}
